import React from 'react';
import clsx from 'clsx';

import Button from '../../../../../../../../common/_custom/Button/Button';
import CrossIcon from '../../../../../../../../assets/Svg/CrossIcon';
import { CHAT_ATTACHMENT_COPY, CHAT_ATTACHMENT_STATUS } from '../../../../../../../../store/dashboard/askAica/constants';
import { AttachmentChipView } from '../../../../../../../../store/dashboard/askAica/types';
import { getFileIcon } from '../FileAttachmentCard/FileAttachmentCard.constants';
import styles from './AttachmentChips.module.scss';

interface AttachmentChipsProps {
    items: AttachmentChipView[];
    /** Omit for a read-only row (chips on a sent message). */
    onRemove?: (id: string) => void;
    disabled?: boolean;
}

const getStatusLabel = (item: AttachmentChipView): string | null => {
    switch (item.status) {
        case CHAT_ATTACHMENT_STATUS.UPLOADING:
            return `Uploading ${item.progress ?? 0}%`;
        // The claim window reads "Uploading 100%" so a bare pill always means sendable.
        case CHAT_ATTACHMENT_STATUS.REGISTERING:
            return CHAT_ATTACHMENT_COPY.UPLOADING_COMPLETE;
        case CHAT_ATTACHMENT_STATUS.READY:
            return null;
        case CHAT_ATTACHMENT_STATUS.PROCESSING:
            return CHAT_ATTACHMENT_COPY.PROCESSING;
        case CHAT_ATTACHMENT_STATUS.FILED:
            return item.companyName ? `${CHAT_ATTACHMENT_COPY.FILED_IN} ${item.companyName}` : 'Filed';
        case CHAT_ATTACHMENT_STATUS.NEEDS_COMPANY:
            return CHAT_ATTACHMENT_COPY.NEEDS_COMPANY;
        default:
            return item.failureReason ? `${CHAT_ATTACHMENT_COPY.FAILED}: ${item.failureReason}` : CHAT_ATTACHMENT_COPY.FAILED;
    }
};

/** One chip per attachment showing its upload / filing state; removable when `onRemove` is given. */
const AttachmentChips: React.FC<AttachmentChipsProps> = ({ items, onRemove, disabled = false }) => {
    if (!items.length) return null;
    return (
        <div className={styles.ChipRow} data-testid="attachment-chips">
            {items.map((item) => {
                const label = getStatusLabel(item);
                return (
                    <div
                        key={item.id}
                        className={clsx(styles.Chip, {
                            [styles.ChipFailed]: item.status === CHAT_ATTACHMENT_STATUS.FAILED,
                            [styles.ChipWithStatus]: label !== null,
                        })}
                        data-testid="attachment-chip"
                    >
                        <img className={styles.FileIcon} src={getFileIcon(item.fileName)} alt="" data-testid="attachment-chip-icon" />
                        <span className={styles.FileName} title={item.fileName}>
                            {item.fileName}
                        </span>
                        {label !== null && (
                            <span className={styles.StatusLabel} data-testid="attachment-chip-status">
                                {label}
                            </span>
                        )}
                        {onRemove && (
                            <Button
                                type="button"
                                className={styles.RemoveButton}
                                onClick={() => onRemove(item.id)}
                                disabled={disabled}
                                aria-label={`${CHAT_ATTACHMENT_COPY.REMOVE_LABEL}: ${item.fileName}`}
                                text={<CrossIcon colour="#4d4d4d" width="12" height="12" />}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AttachmentChips;
